import { useEffect, useRef, useState } from "react";
import { Search, Compass, Rocket } from "lucide-react";
import Reveal from "./Reveal";
import { useI18n } from "../../lib/i18n";

const ICONS = [Search, Compass, Rocket];

export default function HowWeWork() {
  const { t } = useI18n();
  const steps = (t("howWeWork.steps") || []).map((s, i) => ({
    n: String(i + 1).padStart(2, "0"),
    t: s.t,
    d: s.d,
    Icon: ICONS[i] || Search,
  }));
  const sectionRef = useRef(null);
  const [active, setActive] = useState(false);
  const [pulsedSteps, setPulsedSteps] = useState([false, false, false]);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          setActive(true);
          [0, 1, 2].forEach((i) => {
            setTimeout(() => {
              setPulsedSteps((prev) => {
                const next = [...prev];
                next[i] = true;
                return next;
              });
            }, 500 + i * 500);
          });
          obs.disconnect();
        }
      }
    }, { threshold: 0.25 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      data-testid="how-we-work-section"
      className="py-24 md:py-32 bg-surface relative overflow-hidden bg-diagonal-light"
    >
      <div className="container-x">
        <Reveal>
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-6 bg-ember" />
            <span className="text-ember uppercase text-xs tracking-[0.28em] font-body font-medium">
              {t("howWeWork.eyebrow")}
            </span>
          </div>
        </Reveal>
        <Reveal delay={60}>
          <h2 className="font-display uppercase text-primary-fg text-4xl md:text-5xl lg:text-6xl leading-[1]">
            {t("howWeWork.title")}
          </h2>
        </Reveal>

        <div className="relative mt-20">
          {/* Horizontal base line + drawn line — desktop only */}
          <div className="hidden md:block absolute left-0 right-0 top-10 h-px bg-line" />
          <svg
            className="hidden md:block absolute left-0 right-0 top-10 w-full"
            height="2"
            viewBox="0 0 100 2"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <line
              x1="0" y1="1" x2="100" y2="1"
              stroke="#E5503A"
              strokeWidth="2"
              className={`connector-line ${active ? "drawn" : ""}`}
              pathLength="100"
            />
          </svg>

          {/* Vertical connector — mobile only, aligns with center of step rings (w-20 = 80px → center 40px) */}
          <div className="md:hidden absolute top-10 bottom-10 w-px bg-line" style={{ left: "39px" }} aria-hidden="true" />
          <svg
            className="md:hidden absolute w-[2px] top-10 bottom-10"
            style={{ left: "38px" }}
            preserveAspectRatio="none"
            viewBox="0 0 2 100"
            aria-hidden="true"
          >
            <line
              x1="1" y1="0" x2="1" y2="100"
              stroke="#E5503A"
              strokeWidth="2"
              className={`connector-line-v ${active ? "drawn" : ""}`}
              pathLength="100"
            />
          </svg>

          <div className="grid md:grid-cols-3 gap-12 md:gap-8 relative">
            {steps.map((s, i) => (
              <Reveal key={s.n} delay={i * 100}>
                <div data-testid={`step-${s.n}`} className="relative">
                  <div className="relative z-10 flex items-center gap-5">
                    <div className="relative w-20 h-20 shrink-0">
                      {/* Progress ring */}
                      <svg viewBox="0 0 80 80" className={`absolute inset-0 ring-progress ${active ? "drawn" : ""}`}>
                        <circle cx="40" cy="40" r="30" className="ring-track" />
                        <circle cx="40" cy="40" r="30" className="ring-fill" style={{ transitionDelay: `${i * 200}ms` }} />
                      </svg>
                      {/* Icon + pulse */}
                      <div className={`absolute inset-[12px] rounded-full bg-bg border border-ember/40 flex items-center justify-center text-ember ${pulsedSteps[i] ? "step-pulse" : ""}`}>
                        <s.Icon size={20} />
                      </div>
                    </div>
                    <span className="font-display font-extrabold text-ember text-4xl leading-none">
                      {s.n}
                    </span>
                  </div>
                  <h3 className="mt-6 font-display uppercase text-primary-fg text-2xl md:text-3xl">
                    {s.t}
                  </h3>
                  <p className="mt-3 text-secondary-fg font-light text-[15px] leading-relaxed max-w-md">
                    {s.d}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
